export const PATHS = {
  authCallback: '/auth',
  login: '/login',
  rootRedirect: '/',
  businessGroups: '/business-groups',
  dashboard: '/dashboard',
  configuration: '/configuration',
  directoryPermissions: '/directory-permissions',
  contentAccessRoles: '/content-access-roles',
  retentions: '/retentions',
  operators: '/operators',
  landingFolderRestrictions: '/configuration/landing-folder-restrictions',
  userInfo: '/user-info',
  notAuthorized: '/not-authorized',
} as const;

export const WILDCARD = {
  businessGroups: PATHS.businessGroups + '/*',
  dashboard: PATHS.dashboard + '/*',
  configuration: PATHS.configuration + '/*',
  directoryPermissions: PATHS.directoryPermissions + '/*',
  contentAccessRoles: PATHS.contentAccessRoles + '/*',
  retentions: PATHS.retentions + '/*',
  operators: PATHS.operators + '/*',
  landingFolderRestrictions: PATHS.landingFolderRestrictions + '/*',
} as const;

export type AppPath = (typeof PATHS)[keyof typeof PATHS];

export enum AppPathEnum {
  AuthCallback = 'authCallback',
  Login = 'login',
  RootRedirect = 'rootRedirect',
  BusinessGroups = 'businessGroups',
  Dashboard = 'dashboard',
  Configuration = 'configuration',
  DirectoryPermissions = 'directoryPermissions',
  ContentAccessRoles = 'contentAccessRoles',
  Retentions = 'retentions',
  Operators = 'operators',
  LandingFolderRestrictions = 'landingFolderRestrictions',
  UserInfo = 'userInfo',
  NotAuthorized = 'notAuthorized',
}

export type AppUrl = (typeof PATHS)[AppPathEnum];

const DETAIL_SEGMENT = 'detail';
const EDIT_SEGMENT = 'edit';

export const buildBusinessGroupDetail = (id: string | number) =>
  `${PATHS.businessGroups}/${DETAIL_SEGMENT}/${id}`;
export const buildBusinessGroupEdit = (id: string | number) =>
  `${PATHS.businessGroups}/${EDIT_SEGMENT}/${id}`;

export const buildProductDetail = (id: string | number) =>
  `${PATHS.configuration}/products/${DETAIL_SEGMENT}/${id}`;
export const buildProductEdit = (id: string | number) =>
  `${PATHS.configuration}/products/${EDIT_SEGMENT}/${id}`;

export const buildConfigProductDetail = (id: string | number) => buildProductDetail(id);
export const buildConfigProductEdit = (id: string | number) => buildProductEdit(id);

export const buildConfigBusinessGroupDetail = (id: string | number) =>
  `${PATHS.configuration}/business-groups/detail/${id}`;
export const buildConfigBusinessGroupEdit = (id: string | number) =>
  `${PATHS.configuration}/business-groups/edit/${id}`;

export const buildConfigLandingFolderRestrictionDetail = (id: string | number) =>
  `${PATHS.configuration}/landing-folder-restrictions/detail/${id}`;
export const buildConfigLandingFolderRestrictionEdit = (id: string | number) =>
  `${PATHS.configuration}/landing-folder-restrictions/edit/${id}`;

export const buildDetailPath = (base: AppPath, id: string | number) =>
  `${base}/${DETAIL_SEGMENT}/${id}`;
export const buildEditPath = (base: AppPath, id: string | number) =>
  `${base}/${EDIT_SEGMENT}/${id}`;

export interface NavigateDescriptor {
  to: string;
  replace?: boolean;
  state?: unknown;
}

export const asNavigateDescriptor = (
  to: string,
  replace = false,
  state?: unknown,
): NavigateDescriptor => ({
  to,
  replace,
  state,
});

export const pathFromEnum = (key: AppPathEnum): AppPath => PATHS[key];

export const isAppPath = (value: string): value is AppPath =>
  Object.values(PATHS).includes(value as AppPath);

export interface NavigateOptions {
  replace?: boolean;
  state?: unknown;
}

export type NavigateFn = (to: string, opts?: NavigateOptions) => void;

export const navigateToDetail = (
  navigate: NavigateFn,
  base: AppPath,
  id: string | number,
  opts?: NavigateOptions,
) => {
  navigate(buildDetailPath(base, id), opts);
};

export const navigateToEdit = (
  navigate: NavigateFn,
  base: AppPath,
  id: string | number,
  opts?: NavigateOptions,
) => {
  navigate(buildEditPath(base, id), opts);
};
